import React from 'react';

import App, { Container } from 'next/app';
import Head from 'next/head';
import { Provider } from 'mobx-react';
import NextSeo from 'next-seo';

import initializeMailerStore from '../stores/general/MailerStore';
import initializeAlertStore from '../stores/general/AlertStore';
import initializeBlogStore from '../stores/post/BlogStore';
import initializeVirtueStore from '../stores/post/VirtueStore';
import initializeProductStore from '../stores/cart/ProductStore';
import initializeCartStore from '../stores/cart/CartStore';
import initializeCheckoutStore from '../stores/cart/CheckoutStore';
import initializeUserStore from '../stores/UserStore';
import initializeGeneralStore from '../stores/GeneralStore';

const DEFAULT_SEO = {
  title:
    'ThinkVirtues - Leverage proven methods for tapping into your full potential. Courageous. Aspirational. Excellence.',
  description:
    'ThinkVirtues empowers kids to be happy, confident and resilient! ',
  openGraph: {
    type: 'website',
    locale: 'en_US',
    url: 'https://www.thinkvirtues.com',
    title:
      'ThinkVirtues - Leverage proven methods for tapping into your full potential. Courageous. Aspirational. Excellence.',
    image: 'https://i.imgur.com/nkMoVpD.png',
    site_name: 'ThinkVirtues.com',
    imageWidth: 1200,
    imageHeight: 630,
    description:
      'ThinkVirtues empowers kids to be happy, confident and resilient!',
  },
};

class MyMobxApp extends App {
  static async getInitialProps(appCtx) {
    const appContext = { ...appCtx };
    const mailerStore = initializeMailerStore();
    appContext.ctx.mailerStore = mailerStore;

    const alertStore = initializeAlertStore();
    appContext.ctx.alertStore = alertStore;

    const blogStore = initializeBlogStore();
    appContext.ctx.blogStore = blogStore;

    const virtueStore = initializeVirtueStore();
    appContext.ctx.virtueStore = virtueStore;

    const productStore = initializeProductStore();
    appContext.ctx.productStore = productStore;

    const cartStore = initializeCartStore();
    appContext.ctx.cartStore = cartStore;

    const checkoutStore = initializeCheckoutStore();
    appContext.ctx.checkoutStore = checkoutStore;

    const userStore = initializeUserStore();
    appContext.ctx.userStore = userStore;

    const generalStore = initializeGeneralStore();
    appContext.ctx.generalStore = generalStore;

    const appProps = App.getInitialProps(appContext);

    return {
      ...appProps,
      initialMailerStoreState: mailerStore,
      initialAlertStoreState: alertStore,
      initialBlogStoreState: blogStore,
      initialVirtueStoreState: virtueStore,
      initialProductStoreState: productStore,
      initialCartStoreState: cartStore,
      initialCheckoutStoreState: checkoutStore,
      initialUserStoreState: userStore,
      initialGeneralStoreState: generalStore,
    };
  }

  constructor(props) {
    super(props);
    const isServer = !process.browser;

    this.stores = {
      mailerStore: isServer
        ? props.initialMailerStoreState
        : initializeMailerStore(props.initialMailerStoreState),
      alertStore: isServer
        ? props.initialAlertStoreState
        : initializeAlertStore(props.initialAlertStoreState),
      blogStore: isServer
        ? props.initialBlogStoreState
        : initializeBlogStore(props.initialBlogStoreState),
      virtueStore: isServer
        ? props.initialVirtueStoreState
        : initializeVirtueStore(props.initialVirtueStoreState),
      productStore: isServer
        ? props.initialProductStoreState
        : initializeProductStore(props.initialProductStoreState),
      cartStore: isServer
        ? props.initialCartStoreState
        : initializeCartStore(props.initialCartStoreState),
      checkoutStore: isServer
        ? props.initialCheckoutStoreState
        : initializeCheckoutStore(props.initialCheckoutStoreState),
      userStore: isServer
        ? props.initialUserStoreState
        : initializeUserStore(props.initialUserStoreState),
      generalStore: isServer
        ? props.initialGeneralStoreState
        : initializeGeneralStore(props.initialGeneralStoreState),
    };
  }

  componentDidMount() {
    const loggedIn = this.stores.userStore.checkToken();

    this.stores.cartStore.checkOrder(this.stores.userStore.getToken());

    if (loggedIn) {
      this.stores.userStore.show().then((user) => {
        if (user && user.roles && user.roles.indexOf('subscriber') !== -1) {
          this.stores.userStore.getSubscription(user.id);
        }
      });
    }

    if (localStorage.getItem('success')) localStorage.removeItem('success');

    // Gets static pages from WP
    this.stores.generalStore.index();
  }

  render() {
    const { Component, pageProps } = this.props;

    return (
       <>
        <Head>
          <meta name="viewport" content="width=device-width, initial-scale=1" />
          <link href="/static/css/temp/rangeslider.css" rel="stylesheet" />
          <link href="/static/css/temp/slick.css" rel="stylesheet" />
          <link href="/static/css/temp/slick-theme.css" rel="stylesheet" />
          <link href="/static/css/temp/normalize.css" rel="stylesheet" />
          <link href="/static/css/style.css" rel="stylesheet" />
          <script src="https://js.stripe.com/v3/" />
          <link
            rel="shortcut icon"
            type="image/png"
            href="/static/img/favicon.png"
          />
        </Head>
        <Provider stores={this.stores}>
          <React.Fragment>
            <NextSeo config={DEFAULT_SEO} />
            <Component {...pageProps} />
          </React.Fragment>
        </Provider>
        </>
    );
  }
}
export default MyMobxApp;
